import { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import logger from '../utils/logger';
import { enrichEventWithMentions } from '../utils/mentionProcessing';

const ForwardingContext = createContext();

export const useForwarding = () => {
  const context = useContext(ForwardingContext);
  if (!context) {
    throw new Error('useForwarding must be used within a ForwardingProvider');
  }
  return context;
};

const INITIAL_STATE = {
  isForwarding: false,
  showForwardModal: false,
  messageToForward: null,
  sourceRoomId: null,
  messageContent: null,
};

export const ForwardingProvider = ({ children }) => {
  const [forwardingState, setForwardingState] = useState(INITIAL_STATE);

  /**
   * Start forwarding a message - triggers the forward modal
   * @param {object} message - The Matrix message event object
   * @param {string} roomId - The source room ID
   * @param {object} messageContent - Extracted message content for sending
   */
  const startForwarding = useCallback((message, roomId, messageContent) => {
    setForwardingState({
      isForwarding: true,
      showForwardModal: true,
      messageToForward: message,
      sourceRoomId: roomId,
      messageContent,
    });
  }, []);

  /**
   * Cancel forwarding mode and close modal
   */
  const cancelForwarding = useCallback(() => {
    setForwardingState(INITIAL_STATE);
  }, []);

  /**
   * Close the forward modal and reset state
   */
  const closeForwardModal = useCallback(() => {
    setForwardingState(INITIAL_STATE);
  }, []);

  /**
   * Build message content based on message type
   * @param {object} messageContent - The extracted message content
   * @returns {object} Matrix message content ready to send
   */
  const buildMessageContent = useCallback((messageContent) => {
    const content = {
      msgtype: messageContent.msgtype,
    };

    // For attachments, set up the proper structure
    if (messageContent.msgtype !== 'm.text' && messageContent.msgtype !== 'm.emote') {
      // Attachments require: msgtype, body (fallback), url, info, filename
      content.body = messageContent.caption || messageContent.filename || messageContent.body || 'Attachment';
      content.url = messageContent.url;
      content.info = messageContent.info;

      // Include filename for file attachments
      if (messageContent.filename) {
        content.filename = messageContent.filename;
      }

      // Include geo_uri for location messages
      if (messageContent.geo_uri) {
        content.geo_uri = messageContent.geo_uri;
      }
    } else {
      // Text messages just need msgtype and body
      content.body = messageContent.body;
    }

    return content;
  }, []);

  /**
   * Map room members to a simplified format for mention processing
   * @param {object} room - The Matrix room object
   * @returns {Array<{userId: string, name: string, rawDisplayName: string}>}
   */
  const mapRoomMembers = useCallback((room) => {
    const allMembers = room.currentState?.getMembers?.() || [];
    return allMembers
      .filter(m => m.membership === 'join' || m.membership === 'invite')
      .map(m => ({
        userId: m.userId,
        name: m.rawDisplayName || m.name || m.userId,
        rawDisplayName: m.rawDisplayName,
      }));
  }, []);

  /**
   * Forward the message to a target room
   * @param {string} targetRoomId - The room to forward the message to
   * @param {object} client - The Matrix client instance
   * @returns {Promise<void>}
   */
  const forwardToRoom = useCallback(async (targetRoomId, client) => {
    const { messageToForward, messageContent } = forwardingState;

    if (!messageToForward || !messageContent) {
      throw new Error('No message to forward');
    }

    if (!client) {
      throw new Error('Matrix client not available');
    }

    try {
      const room = client.getRoom(targetRoomId);
      if (!room) {
        throw new Error('Target room not found');
      }

      // Build message content using helper
      const content = buildMessageContent(messageContent);

      // Enrich text/emote messages with mentions resolved against target room members
      let enrichedContent = content;
      if (content.msgtype === 'm.text' || content.msgtype === 'm.emote') {
        const roomMembers = mapRoomMembers(room);

        if (roomMembers.length > 0) {
          const currentUserId = client.getUserId?.();
          enrichedContent = enrichEventWithMentions(content, roomMembers, currentUserId);
        }
      }

      // Send the message
      await client.sendMessage(targetRoomId, enrichedContent);

      // Clear forwarding state
      setForwardingState(INITIAL_STATE);
    } catch (error) {
      logger.error('[ForwardingContext] Failed to forward message:', error);
      throw error;
    }
  }, [forwardingState, buildMessageContent, mapRoomMembers]);

  /**
   * Forward the message to multiple target rooms
   * @param {string[]} targetRoomIds - Array of room IDs to forward to
   * @param {object} client - The Matrix client instance
   * @returns {Promise<Array<{roomId: string, success: boolean, error?: string}>>}
   */
  const forwardToRooms = useCallback(async (targetRoomIds, client) => {
    const { messageToForward, messageContent } = forwardingState;

    if (!messageToForward || !messageContent) {
      throw new Error('No message to forward');
    }

    if (!client) {
      throw new Error('Matrix client not available');
    }

    if (!targetRoomIds || targetRoomIds.length === 0) {
      throw new Error('No target rooms specified');
    }

    const results = [];
    const baseContent = buildMessageContent(messageContent);
    const currentUserId = client.getUserId?.();

    for (const roomId of targetRoomIds) {
      try {
        const room = client.getRoom(roomId);
        if (!room) {
          results.push({ roomId, success: false, error: 'Room not found' });
          continue;
        }

        // Enrich text/emote messages with mentions resolved against this room's members
        let contentToSend = baseContent;
        if (baseContent.msgtype === 'm.text' || baseContent.msgtype === 'm.emote') {
          const roomMembers = mapRoomMembers(room);

          if (roomMembers.length > 0) {
            contentToSend = enrichEventWithMentions(baseContent, roomMembers, currentUserId);
          }
        }

        await client.sendMessage(roomId, contentToSend);
        results.push({ roomId, success: true });
      } catch (error) {
        logger.error('[ForwardingContext] Failed to forward to room', { error: error.message });
        results.push({ roomId, success: false, error: error.message });
      }
    }

    const successCount = results.filter(r => r.success).length;

    // Clear state if at least one forward succeeded
    if (successCount > 0) {
      setForwardingState(INITIAL_STATE);
    }

    return results;
  }, [forwardingState, buildMessageContent, mapRoomMembers]);

  const value = {
    isForwarding: forwardingState.isForwarding,
    showForwardModal: forwardingState.showForwardModal,
    sourceRoomId: forwardingState.sourceRoomId,
    messageToForward: forwardingState.messageToForward,
    messageContent: forwardingState.messageContent,
    startForwarding,
    cancelForwarding,
    closeForwardModal,
    forwardToRoom,
    forwardToRooms,
  };

  return (
    <ForwardingContext.Provider value={value}>
      {children}
    </ForwardingContext.Provider>
  );
};

ForwardingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ForwardingContext;
